import * as React from 'react';
import Form from 'antd/lib/form';
import Select from 'antd/lib/select';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import { FilterOption } from '@/types/summaryView';
import { isArchetypeProperty, isStatisticProperty, getSummaryViewPropertyLabel } from '@/helpers/summaryViewHelpers';
import { antGrey } from '@/components/styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Flex from 'antd/lib/flex';
import Alert from 'antd/lib/alert';
const { Option, OptGroup } = Select;

interface FiltersPopoverProps {
    isVisible: boolean;
    onApply: (metrics: string[], versions: string[]) => void;
    onTrack?: (metrics: string[], versions: string[]) => void;
    currentMetrics: string[];
    currentVersions: string[];
    availableMetrics: FilterOption[];
    availableVersions: string[];
    isSubmitting: boolean;
    defaultMetrics?: string[];
    defaultVersions?: string[];
}

const groupMetrics = (metrics: FilterOption[]) => {
    const standardMetrics: FilterOption[] = [];
    const stepDetails: FilterOption[] = [];
    const statistics: FilterOption[] = [];

    metrics.forEach((metric) => {
        if (isArchetypeProperty(metric.key)) {
            stepDetails.push(metric);
        } else if (isStatisticProperty(metric.key)) {
            statistics.push(metric);
        } else {
            standardMetrics.push(metric);
        }
    });

    return { standardMetrics, stepDetails, statistics };
};

export const FiltersPopover: React.FC<FiltersPopoverProps> = (props) => {
    const [form] = Form.useForm();
    const versions = Form.useWatch('versions', form) || [];
    const hasNoVersions = versions.length === 0;

    const { standardMetrics, stepDetails, statistics } = React.useMemo(
        () => groupMetrics(props.availableMetrics),
        [props.availableMetrics],
    );

    const applyFilters = (values: { metrics?: string[]; versions?: string[] }) => {
        const metrics = values.metrics || [];
        const versions = values.versions || [];
        props.onApply(metrics, versions);
        props.onTrack?.(metrics, versions);
    };

    const clearFilters = () => {
        const resetMetrics =
            props.defaultMetrics?.length ? props.defaultMetrics : props.availableMetrics.map((m) => m.key);
        const resetVersions = props.defaultVersions?.length ? props.defaultVersions : props.availableVersions;
        form.setFieldsValue({
            metrics: resetMetrics,
            versions: resetVersions,
        });
    };

    const prevIsVisible = React.useRef(false);

    React.useEffect(() => {
        if (props.isVisible && !prevIsVisible.current) {
            form.setFieldsValue({
                metrics: props.currentMetrics,
                versions: props.currentVersions,
            });
        }
        prevIsVisible.current = props.isVisible;
    }, [props.isVisible, props.currentMetrics, props.currentVersions, form]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <div
                style={{
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingTop: 18,
                    paddingBottom: 18,
                    maxHeight: 450,
                    overflowY: 'scroll',
                }}
            >
                <Form
                    form={form}
                    initialValues={{
                        metrics: props.currentMetrics,
                        versions: props.currentVersions,
                    }}
                    onFinish={applyFilters}
                    preserve={false}
                    layout="vertical"
                >
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        <Form.Item label="Attributes" name="metrics" style={{ marginBottom: 0 }}>
                            <Select
                                mode="multiple"
                                placeholder="Select attributes"
                                allowClear
                                showSearch
                                optionFilterProp="children"
                                maxTagCount={'responsive'}
                                style={{ width: 400 }}
                            >
                                {standardMetrics.length > 0 && (
                                    <OptGroup label="Standard Attributes">
                                        {standardMetrics.map((metric) => (
                                            <Option key={metric.key} value={metric.key}>
                                                {metric.label}
                                            </Option>
                                        ))}
                                    </OptGroup>
                                )}
                                {stepDetails.length > 0 && (
                                    <OptGroup label="Step Details">
                                        {stepDetails.map((metric) => (
                                            <Option key={metric.key} value={metric.key}>
                                                {getSummaryViewPropertyLabel(metric.key)}
                                            </Option>
                                        ))}
                                    </OptGroup>
                                )}
                                {statistics.length > 0 && (
                                    <OptGroup label="Statistics">
                                        {statistics.map((metric) => (
                                            <Option key={metric.key} value={metric.key}>
                                                {metric.label}
                                            </Option>
                                        ))}
                                    </OptGroup>
                                )}
                            </Select>
                        </Form.Item>

                        <Form.Item name="versions" style={{ marginBottom: 0 }}>
                            <Flex vertical>
                                <Flex style={{ paddingBottom: 8 }} justify="space-between">
                                    <Text style={{ fontSize: 14, color: antGrey.grey8 }}> Versions </Text>
                                    <Button
                                        type="link"
                                        onClick={() => {
                                            const all = props.availableVersions;
                                            form.setFieldsValue({ versions: all });
                                        }}
                                        style={{ padding: 0, height: 'fit-content' }}
                                    >
                                        Select all
                                    </Button>
                                </Flex>
                                <Form.Item name="versions" noStyle>
                                    <Select
                                        mode="multiple"
                                        placeholder="Select versions"
                                        allowClear
                                        showSearch
                                        optionFilterProp="children"
                                        maxTagCount={'responsive'}
                                        style={{ width: 400 }}
                                    >
                                        {props.availableVersions.map((version) => (
                                            <Option key={version} value={version}>
                                                {version}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Flex>
                        </Form.Item>
                    </Space>
                </Form>
            </div>
            {hasNoVersions && <Alert message="Please select at least one version" type="warning" banner={true} />}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 8,
                    paddingLeft: 16,
                    paddingRight: 16,
                    paddingTop: 12,
                    paddingBottom: 12,
                    backgroundColor: antGrey.grey3,
                }}
            >
                <Button size="small" onClick={clearFilters} disabled={props.isSubmitting}>
                    Clear
                </Button>
                <Button
                    size="small"
                    onClick={form.submit}
                    type="primary"
                    loading={props.isSubmitting}
                    disabled={hasNoVersions}
                >
                    Apply
                </Button>
            </div>
        </div>
    );
};
